import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  applyDocumentColorMode,
  DEFAULT_COLOR_MODE_PREFERENCE,
  readStoredColorMode,
  resolveColorMode,
  subscribeSystemColorScheme,
  writeStoredColorMode,
} from './colorMode'

const ColorModeContext = createContext({
  preference: DEFAULT_COLOR_MODE_PREFERENCE,
  resolvedMode: 'light',
  setPreference: () => {},
})

export function ColorModeProvider({ children  }) {
  const [preference, setPreferenceState] = useState(() => {
    const pref = readStoredColorMode()
    applyDocumentColorMode(resolveColorMode(pref))
    return pref
  })
  const [systemMode, setSystemMode] = useState(() =>
    resolveColorMode('auto'),
  )

  const resolvedMode = useMemo(
    () => resolveColorMode(preference),
    // Re-resolve when OS preference changes under Auto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preference, systemMode],
  )

  useEffect(() => {
    applyDocumentColorMode(resolvedMode)
  }, [resolvedMode])

  useEffect(() => {
    return subscribeSystemColorScheme((mode) => {
      setSystemMode(mode)
    })
  }, [])

  const setPreference = useCallback((next) => {
    const value =
      next === 'light' || next === 'dark' || next === 'auto'
        ? next
        : DEFAULT_COLOR_MODE_PREFERENCE
    setPreferenceState(value)
    writeStoredColorMode(value)
  }, [])

  const value = useMemo(
    () => ({
      preference,
      resolvedMode,
      setPreference,
    }),
    [preference, resolvedMode, setPreference],
  )

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  )
}

export function useColorMode() {
  return useContext(ColorModeContext)
}

export default ColorModeProvider
